import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { normalizeCompanyBrandingPayload, resolveCompanyBrandingName } from './wpaCompanyBranding';
import WpaPwaManifestAndInstall from './WpaPwaManifestAndInstall';

const PRIMARY = '#4CAF50';
const PRIMARY_DARK = '#388E3C';
const BG = '#f5f7f5';
const CARD_SHADOW = '0 4px 24px rgba(0,0,0,0.10)';

const styles = {
  root: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: BG,
    padding: 'max(16px, env(safe-area-inset-top)) max(16px, env(safe-area-inset-right)) max(16px, env(safe-area-inset-bottom)) max(16px, env(safe-area-inset-left))',
    boxSizing: 'border-box',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  card: {
    width: '100%',
    maxWidth: 400,
    background: '#fff',
    borderRadius: 16,
    boxShadow: CARD_SHADOW,
    padding: '32px 28px 28px',
    boxSizing: 'border-box',
  },
  title: { margin: '0 0 8px', fontSize: 22, fontWeight: 700, color: '#222' },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 6 },
  input: {
    width: '100%',
    padding: '12px 14px',
    fontSize: 15,
    border: '1px solid #ddd',
    borderRadius: 10,
    outline: 'none',
    boxSizing: 'border-box',
    marginBottom: 14,
  },
  btn: {
    width: '100%',
    padding: '13px 0',
    fontSize: 16,
    fontWeight: 600,
    color: '#fff',
    background: PRIMARY,
    border: 'none',
    borderRadius: 10,
    cursor: 'pointer',
    marginTop: 4,
  },
  btnDisabled: { background: '#a5d6a7', cursor: 'not-allowed' },
  error: {
    background: '#fff5f5',
    color: '#c0392b',
    borderRadius: 8,
    padding: '10px 14px',
    fontSize: 13,
    marginBottom: 14,
    border: '1px solid #f5c6cb',
  },
  success: {
    background: '#e8f5e9',
    color: '#2e7d32',
    borderRadius: 8,
    padding: '10px 14px',
    fontSize: 13,
    marginBottom: 14,
    border: '1px solid #c8e6c9',
  },
  link: {
    background: 'none',
    border: 'none',
    padding: '12px 0 0',
    color: PRIMARY,
    fontSize: 14,
    cursor: 'pointer',
    fontFamily: 'inherit',
    textDecoration: 'underline',
    width: '100%',
    textAlign: 'center',
  },
  spinner: {
    display: 'inline-block',
    width: 18,
    height: 18,
    border: '3px solid rgba(255,255,255,0.3)',
    borderTop: '3px solid #fff',
    borderRadius: '50%',
    animation: 'wpa-spin 0.7s linear infinite',
    verticalAlign: 'middle',
    marginRight: 8,
  },
};

const css = `@keyframes wpa-spin { to { transform: rotate(360deg); } }`;

const Spinner = () => <span style={styles.spinner} />;

const WpaResetPasswordPage = () => {
  const { t, currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const { companyId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [companyBranding, setCompanyBranding] = useState(() => ({ name: '', nameLocales: null }));
  const documentTitleBeforeResetRef = useRef(null);

  const companyDisplayName = useMemo(
    () => resolveCompanyBrandingName(companyBranding, currentLanguage),
    [companyBranding, currentLanguage],
  );

  const goLogin = useCallback(() => {
    navigate(`/${companyId}/wpa/login`);
  }, [companyId, navigate]);

  useEffect(() => {
    if (!companyId) return;
    (async () => {
      try {
        const res = await fetch(`/api/wpa-auth/company-branding/${companyId}`);
        if (!res.ok) return;
        const data = await res.json();
        setCompanyBranding(normalizeCompanyBrandingPayload(data));
      } catch {
        // ignore
      }
    })();
  }, [companyId]);

  useEffect(() => {
    if (documentTitleBeforeResetRef.current === null) {
      documentTitleBeforeResetRef.current = document.title;
    }
    document.title = companyDisplayName || t('wpaLogin.resetPasswordTitle');
  }, [companyDisplayName, t]);

  useEffect(() => {
    return () => {
      const previousTitle = documentTitleBeforeResetRef.current;
      if (previousTitle != null) document.title = previousTitle;
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!token.trim()) {
      setError(t('wpaLogin.resetPasswordTokenMissing'));
      return;
    }
    if (password.length < 8) {
      setError(t('wpaLogin.passwordMinLength'));
      return;
    }
    if (password !== confirm) {
      setError(t('wpaLogin.forgotPasswordMismatch'));
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('/api/wpa-auth/forgot-password/confirm-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyId,
          token: token.trim(),
          newPassword: password,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setError(data.message || t('wpaLogin.resetPasswordTokenMissing'));
        return;
      }
      setDone(true);
    } catch {
      setError(t('wpaLogin.networkError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <WpaPwaManifestAndInstall companyId={companyId} />
      <style>{css}</style>
      <div style={styles.root}>
        <div style={styles.card}>
          <h1 style={styles.title}>{t('wpaLogin.resetPasswordTitle')}</h1>
          {!token.trim() && <div style={styles.error}>{t('wpaLogin.resetPasswordTokenMissing')}</div>}
          {done ? (
            <>
              <div style={styles.success}>{t('wpaLogin.resetPasswordSuccess')}</div>
              <button type="button" style={styles.link} onClick={goLogin}>
                {t('wpaLogin.resetPasswordBackToLogin')}
              </button>
            </>
          ) : (
            <form onSubmit={handleSubmit}>
              {error ? <div style={styles.error}>{error}</div> : null}
              <label style={styles.label}>{t('wpaLogin.forgotPasswordNewPassword')}</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={styles.input}
                autoComplete="new-password"
                minLength={8}
                required
              />
              <label style={styles.label}>{t('wpaLogin.forgotPasswordConfirmPassword')}</label>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                style={styles.input}
                autoComplete="new-password"
                minLength={8}
                required
              />
              <button
                type="submit"
                disabled={loading || !token.trim()}
                style={{ ...styles.btn, ...(loading || !token.trim() ? styles.btnDisabled : {}) }}
                onMouseEnter={(ev) => { if (!loading && token.trim()) ev.target.style.background = PRIMARY_DARK; }}
                onMouseLeave={(ev) => { if (!loading && token.trim()) ev.target.style.background = PRIMARY; }}
              >
                {loading ? <><Spinner />{t('wpaLogin.verifying')}</> : t('wpaLogin.resetPasswordSubmit')}
              </button>
              <button type="button" style={styles.link} onClick={goLogin}>
                {t('wpaLogin.resetPasswordBackToLogin')}
              </button>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default WpaResetPasswordPage;
